import * as React from 'react';
import { legacy_connect as connect } from 'react-redux';
import get from 'lodash/get';
import { ComplexValue } from 'ui';
import { currentUser as selectCurrentUser } from 'app/area/case/selectors/current-user/current-user';
import { caseId as selectCaseId } from 'app/area/case/selectors/case-id';
import { casePermissions as selectCasePermissions } from 'app/area/case/selectors/case-permissions';
import { canAddNewEntry as selectCanAddNewEntry } from 'app/area/case/selectors/involved-person-entry-log-values';
import { actionCreators } from '../actions';
import { caseActionCreators } from '../case-actions';
import { StoreItemPath } from 'app/types-shared';

type WithNewEntryOwnProps = {
  canAddNewEntry?: boolean;
  openEntryPath?: StoreItemPath[];
  textNewEntry?: string;
};

type WithNewEntryDispatchProps = {
  addEntry: (parentPath: StoreItemPath[], currentUser?: ComplexValue<number>, caseId?: number) => Promise<any>;
  egressEntry: (entryPath: StoreItemPath[], currentUser?: ComplexValue<number>, caseId?: number) => Promise<any>;
};

/**
 * How entries are added and egressed. Differs between editing a draft and reading a case.
 */
type EntryLogOperations = {
  addEntry: (dispatch: any, parentPath: StoreItemPath[], user?: ComplexValue<number>, caseId?: number) => Promise<any>;
  egressEntry: (dispatch: any, entryPath: StoreItemPath[], user?: ComplexValue<number>, caseId?: number) => Promise<any>;
  /**
   * Extra props read from the store. Row enhancers wrap the connected row, so they never receive
   * the props the component's own selectors produce and must select what they need themselves.
   */
  selectProps?: (areaState: any, uiProps: any) => Record<string, unknown>;
  /**
   * Whether the user has permission to edit the case. 
   */
  hasCaseEditPermission?: (areaState: any, uiProps: any) => boolean;
};

/**
 * A change can take a while to save. Ignores calls until the previous one has finished
 * and reports whether a change is currently being saved.
 */
const useRunOnce = () => {
  const isBusy = React.useRef(false);
  const isMounted = React.useRef(true);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const runOnce = async (operation: () => Promise<any>) => {
    if (isBusy.current) {
      return null;
    }
    isBusy.current = true;
    setIsSaving(true);
    try {
      return await operation();
    } finally {
      isBusy.current = false;
      isMounted.current && setIsSaving(false);
    }
  };

  return { runOnce, isSaving };
};

const createNewEntry = (operations: EntryLogOperations) => (Component: any, uiProps: any) => {
  const stateToProps = (appState: any) => {
    const areaState = get(appState, uiProps.statePath);
    const user = selectCurrentUser(areaState, uiProps, appState).currentUser;
    const entryLogProps = operations.hasCaseEditPermission?.(areaState, uiProps) === false ?
      { canAddNewEntry: false, openEntryPath: undefined } :
      selectCanAddNewEntry(areaState, uiProps);
    return {
      ...entryLogProps,
      ...operations.selectProps?.(areaState, uiProps),
      userId: user.value,
      userName: user.description
    };
  };

  const dispatchToProps = (dispatch: any): WithNewEntryDispatchProps => ({
    addEntry: (parentPath, user, caseId) => operations.addEntry(dispatch, parentPath, user, caseId),
    egressEntry: (entryPath, user, caseId) => operations.egressEntry(dispatch, entryPath, user, caseId)
  });

  const WrappedComponent = (props: WithNewEntryOwnProps & WithNewEntryDispatchProps & { userId?: number; userName?: string }) => {
    const { addEntry, egressEntry, openEntryPath, userId, userName, ...remainingProps } = props;
    const { canAddNewEntry, textNewEntry } = props;
    const caseId = (props as any).caseId;
    const currentUser = userId !== undefined ? { value: userId, description: userName } : undefined;
    const { runOnce, isSaving } = useRunOnce();

    // The handlers resolve with the outcome, e.g. `{ saved: true }`, so the component can tell the user.
    // Texts for that are properties of the component itself, which row enhancers do not receive.
    const onEgressEntry = openEntryPath ?
      () => runOnce(() => egressEntry(openEntryPath, currentUser, caseId)) : undefined;

    const onAddNewEntry = () => runOnce(() => addEntry(uiProps.resolveContext.path, currentUser, caseId));

    const allowAddNewEntry = !!canAddNewEntry;

    return <Component
      {...remainingProps}
      canAddNewEntry={allowAddNewEntry}
      onAddNewEntry={allowAddNewEntry ? onAddNewEntry : undefined}
      onEgressEntry={onEgressEntry}
      isSaving={isSaving}
      textNewEntry={textNewEntry} />;
  };

  return connect<any, any, any>(stateToProps, dispatchToProps)(WrappedComponent);
};


export const withNewEntry = createNewEntry({
  addEntry: (dispatch, parentPath, user) => dispatch(actionCreators.onAddInvolvedPersonEntry(parentPath, user)),
  egressEntry: (dispatch, entryPath, user) => dispatch(actionCreators.onEgressInvolvedPersonEntry(entryPath, user))
});


export const withNewEntryRead = createNewEntry({
  addEntry: (dispatch, parentPath, user, caseId) =>
    dispatch(caseActionCreators.onAddInvolvedPersonEntry(caseId, parentPath, user)),
  egressEntry: (dispatch, entryPath, user, caseId) =>
    dispatch(caseActionCreators.onEgressInvolvedPersonEntry(caseId, entryPath, user)),
  selectProps: (areaState, uiProps) => selectCaseId(areaState, uiProps),
  hasCaseEditPermission: (areaState, uiProps) => !!selectCasePermissions(areaState, uiProps)?.enabledEntityOperations?.edit
});
